import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getLeaderBoardList } from '../../../redux/actions/dashboardActions';
import { selectCurrentUser } from '../../../selectors/authSelectors';
import { selectLeaderboardList, selectLeaderboardListLoading } from '../../../selectors/dashboardSelectors';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';
import SeeMoreButton from '../../shared/SeeMoreButton/SeeMoreButton';
import { EmptyLeaderboard } from '../EmptyLeaderBoard/EmptyLeaderboard';

import { EllipsisDots } from './EllipsisDots/EllipsisDots';
import { LeaderboardItem } from './LeaderboardItem/LeaderboardItem';
import { PeriodSelector } from './PeriodSelector/PeriodSelector';

export const Leaderboard = () => {
  const { t } = useTranslation('dashboard');
  const dispatch = useDispatch();
  const [displayCount] = useState(5);
  const [period, setPeriod] = useState('week');
  const leaderboardList = useSelector(selectLeaderboardList);
  const leaderboardListLoading = useSelector(selectLeaderboardListLoading);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const currentUser = useSelector(selectCurrentUser);

  useEffect(() => {
    if (currentOrganization) {
      dispatch(getLeaderBoardList({ filter: period, organization_id: currentOrganization.id }));
    }
  }, [dispatch, period, currentOrganization?.id]);

  // Check if current user is not in the top displayed items but exists in the list
  const shouldShowCurrentUser =
    currentUser &&
    !leaderboardList?.slice(0, displayCount).find((item) => item.user.id === currentUser.id) &&
    leaderboardList?.find((item) => item.user.id === currentUser.id);

  // Find current user's item and rank
  const getUserData = () => {
    if (!shouldShowCurrentUser) return null;
    const userItem = leaderboardList.find((item) => item.user.id === currentUser.id);
    const userRank = leaderboardList.findIndex((item) => item.user.id === currentUser.id) + 1;
    return { userItem, userRank };
  };

  return (
    <div className="bg-white rounded-xl shadow-card">
      <div className="p-3">
        <div className="flex flex-wrap items-center justify-between gap-2 sm:flex-nowrap">
          <div className="flex flex-col">
            <p className="text-base font-semibold text-gray-900 sm:text-lg mb-0">{t('leaderboard.title')}</p>
            <p className="text-xs text-gray-600 mb-0">{t('leaderboard.referrals')}</p>
          </div>
          <div className="flex-shrink-0">
            <PeriodSelector period={period} setPeriod={setPeriod} />
          </div>
        </div>
      </div>

      {currentOrganization?.name === 'Boon' || leaderboardList?.length === 0 || leaderboardListLoading ? (
        <EmptyLeaderboard />
      ) : (
        <div className="flex flex-col gap-2 px-3 pb-3">
          {leaderboardList?.slice(0, 5).map((item, index) => (
            <LeaderboardItem key={item.id || index} item={item} rank={index + 1} currentUserId={currentUser?.id} />
          ))}

          {shouldShowCurrentUser && (
            <>
              <EllipsisDots key="ellipsis" />
              <LeaderboardItem
                key={`current-user-${currentUser.id}`}
                item={getUserData().userItem}
                rank={getUserData().userRank}
                currentUserId={currentUser?.id}
              />
            </>
          )}

          {leaderboardList?.length > 5 && (
            <div className="flex justify-center">
              <SeeMoreButton label={t('seeMore')} tab="/dashboard" path="/leaderboard" className="w-fit" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
